import {
  Box,
  Button,
  Heading,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react';
import { dataAttr } from '@chakra-ui/utils';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import CopyIcon from '@/assets/icons/ic-copy.svg';

import { HighlightableText } from '@/components/ui/core/HighlightableText/HighlightableText';
import { queryAllLectureProfilesByLectureId } from '@/fetchers/lectureProfiles';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { ThinEvent } from '@/resources/ThinEvent';
import { ThinLecture } from '@/resources/ThinLecture';
import { dateToString } from '@/utils/date';

interface ICiteLectureModalProps {
  lectureId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const CiteLectureModal: FC<ICiteLectureModalProps> = ({ lectureId, isOpen, onClose }) => {
  const [showNotification, setShowNotification] = useState(null);
  const [isCopied, setCopied] = useState(false);
  const { t } = useTranslation();

  const [clipboardState, copyToClipboard] = useCopyToClipboard();

  const { data: lectureProfiles } = useResources(() => (isOpen ? queryAllLectureProfilesByLectureId(lectureId) : null));

  const { data: lecture } = useResource(() => (isOpen ? [ThinLecture, lectureId] : null));

  const { data: event } = useResource(() => (lecture?.eventId && isOpen ? [ThinEvent, lecture.eventId] : null));

  const getCitation = useMemo(() => {
    const eventDate = dateToString(lecture?.heldAt, 'MMM dd yyyy');

    const doiNumber = lecture?.underlineDoi ? ` DOI: ${lecture.underlineDoi}.` : '';
    const profileNames = lectureProfiles?.map((lectureProfile) => lectureProfile?.profile?.fullName).join(', ');
    const eventName = event?.name ? `${event.name}, ` : '';

    return `${profileNames} (${eventDate}). ${lecture?.title}, ${eventName} Underline Science Inc.${doiNumber}`;
  }, [lecture, lectureProfiles, event]);

  const availableFrom = `${t('newLecture.citeAvailable')}: ${process.env.NEXT_PUBLIC_BASE_URL}${lecture?.linkSlug}`;

  const onCite = () => {
    copyToClipboard(`${getCitation} ${availableFrom}`);

    const message = clipboardState.error ? t('newLecture.copySuccess') : t('newLecture.copyError');

    setCopied(true);
    setShowNotification(message);

    setTimeout(() => {
      setShowNotification(null);
      setCopied(false);
    }, 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered variant="inverted">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Heading as="h4" fontSize="2rem" lineHeight="10">
            {t('newLecture.citeTitle')}
          </Heading>
          <HighlightableText data-highlighted={dataAttr(isCopied)} mt={8} mb={4} fontSize="md">
            {getCitation}
          </HighlightableText>
          <HighlightableText data-highlighted={dataAttr(isCopied)} fontSize="md">
            {availableFrom}
          </HighlightableText>
        </ModalBody>
        <ModalFooter>
          <Box position="relative">
            <Text fontSize="sm" position="absolute" top="-10px" aria-live="polite" aria-atomic="true">
              {showNotification ? t('newLecture.copySuccess') : ''}
            </Text>

            <Button
              type="button"
              variant="unstyled"
              display="flex"
              color="white"
              leftIcon={<CopyIcon width="24px" />}
              onClick={onCite}
            >
              {t('newLecture.citeCopyButton')}
            </Button>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
